import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Plus, UserPlus, Users } from "lucide-react";
import { Button } from "components/ui/Button";
import { StatCard } from "components/ui/Card";
import { EmailingOverview, EmailingStatus, MailTemplate, RecipientRef, UserSegment, getEmailingOverview, getEmailingStatus, getMailTemplates } from "interfaces/emailing";
import Composer from "./emailing/Composer";
import Recipients from "./emailing/Recipients";
import Prospects from "./emailing/Prospects";
import RecipientDrawer from "./emailing/RecipientDrawer";
import Scheduled from "./emailing/Scheduled";
import { SEGMENTS } from "./emailing/parts";

/**
 * /bo/emailing — emails from the sales team, with the account's Resend templates, to app users and
 * to prospects who have no account yet. Two lists (`?view=users|prospects`, segment in `?segment=`),
 * each showing how many emails every person got and the last one; a click opens their full history.
 */
type View = "users" | "prospects";

export default function Emailing() {
    const [params, setParams] = useSearchParams();
    const view: View = params.get("view") === "prospects" ? "prospects" : "users";
    const segment = (SEGMENTS.some((s) => s.id === params.get("segment")) ? params.get("segment") : "all") as UserSegment;

    const [status, setStatus] = useState<EmailingStatus | null>(null);
    const [overview, setOverview] = useState<EmailingOverview | null>(null);
    const [templates, setTemplates] = useState<MailTemplate[]>([]);
    const [composer, setComposer] = useState<RecipientRef[] | null>(null);
    const [open, setOpen] = useState<RecipientRef | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = () => setRefreshKey((k) => k + 1);

    const setParam = (key: string, value: string | null) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value === null) next.delete(key);
                else next.set(key, value);
                return next;
            },
            { replace: true }
        );

    useEffect(() => {
        getEmailingStatus().then(setStatus).catch(() => setStatus(null));
    }, []);
    useEffect(() => {
        getEmailingOverview(30).then(setOverview).catch(() => setOverview(null));
    }, [refreshKey]);
    // Resend templates, re-read each time the composer opens (the backend syncs at most once a minute).
    const composerOpen = composer !== null;
    useEffect(() => {
        getMailTemplates()
            .then(setTemplates)
            .catch(() => setTemplates([]));
    }, [composerOpen]);

    const views: { id: View; label: string; count?: number; icon: typeof Users }[] = [
        { id: "users", label: "Utilisateurs", count: overview?.users.all, icon: Users },
        { id: "prospects", label: "Prospects sans compte", count: overview?.contacts, icon: UserPlus },
    ];

    return (
        <div className="min-h-full bg-canvas">
            <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 md:px-8">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Emails clients</h1>
                        <p className="mt-1 text-sm text-ink-muted">Écrire aux utilisateurs et aux prospects avec les modèles Resend, et retrouver tout ce que chacun a reçu.</p>
                    </div>
                    <Button variant="accent" onClick={() => setComposer([])}>
                        <Plus className="h-4 w-4" />
                        Nouvel email
                    </Button>
                </div>

                {status && !status.resend.ok && (
                    <div className="flex items-start gap-3 rounded-2xl border border-danger/20 bg-danger-subtle p-4 text-sm text-ink">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                        <p>
                            Resend n'est pas configuré : ajoutez <span className="font-mono">{status.resend.missing.join(", ")}</span> aux variables d'environnement du serveur. L'envoi est
                            désactivé.
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <StatCard label="Emails envoyés (30 j)" value={overview?.emails.sent ?? "—"} />
                    <StatCard label="Personnes contactées (30 j)" value={overview?.emails.recipients ?? "—"} />
                    <StatCard label="Utilisateurs" value={overview?.users.all ?? "—"} />
                    <StatCard label="Prospects sans compte" value={overview?.contacts ?? "—"} />
                </div>

                <Scheduled refreshKey={refreshKey} onChanged={refresh} />

                <nav className="flex gap-6 border-b border-border" aria-label="Destinataires">
                    {views.map(({ id, label, count, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setParam("view", id === "users" ? null : id)}
                            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors duration-150 ${
                                view === id ? "border-accent text-ink" : "border-transparent text-ink-muted hover:text-ink"
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                            {count !== undefined && <span className="text-xs text-ink-faint">{count}</span>}
                        </button>
                    ))}
                </nav>

                {view === "users" ? (
                    <Recipients
                        overview={overview}
                        segment={segment}
                        setSegment={(s) => setParam("segment", s === "all" ? null : s)}
                        refreshKey={refreshKey}
                        onCompose={setComposer}
                        onOpen={setOpen}
                    />
                ) : (
                    <Prospects refreshKey={refreshKey} onCompose={setComposer} onOpen={setOpen} onChanged={refresh} />
                )}
            </div>

            {open && (
                <RecipientDrawer
                    kind={open.kind}
                    id={open._id}
                    refreshKey={refreshKey}
                    onClose={() => setOpen(null)}
                    onSend={(r) => setComposer([r])}
                    onChanged={refresh}
                />
            )}
            {composer && <Composer status={status} templates={templates} initialRecipients={composer} onClose={() => setComposer(null)} onSent={refresh} />}
        </div>
    );
}
